/**
 * IconToggle — botão liga/desliga com ícones (ToggleRight / ToggleLeft).
 *
 * Padrão visual adotado a partir da listagem de Departamentos
 * (src/pages/tenant/Departments.tsx) e replicado em telas que precisam de
 * um controle binário compacto, em substituição ao componente Switch.
 *
 * - Ativo  → ícone ToggleRight em verde (text-emerald-600)
 * - Inativo → ícone ToggleLeft cinza (text-muted-foreground)
 * - Estado `disabled` reduz opacidade e bloqueia clique.
 * - Tooltip opcional com rótulos contextuais (ex.: "Ativar"/"Desativar").
 *
 * @version 1.0.0 - 2026-06-05 — Criado para unificar o padrão de toggles.
 */
import * as React from 'react';
import { ToggleLeft, ToggleRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface IconToggleProps {
  /** Estado atual do toggle. */
  checked: boolean;
  /** Callback disparado ao alternar. Recebe o NOVO valor. */
  onCheckedChange?: (next: boolean) => void;
  /** Bloqueia o controle (sem clique, opacidade reduzida). */
  disabled?: boolean;
  /** Texto acessível (aria-label) — obrigatório para leitores de tela. */
  'aria-label'?: string;
  /** Rótulos do tooltip. Quando omitidos, o tooltip não é renderizado. */
  tooltipOn?: string;
  tooltipOff?: string;
  /** Classes extras para o botão. */
  className?: string;
}

/**
 * Toggle compacto baseado em ícone — substitui Switch onde quisermos o
 * mesmo visual já consolidado na lista de departamentos.
 */
export function IconToggle({
  checked,
  onCheckedChange,
  disabled,
  tooltipOn,
  tooltipOff,
  className,
  ...rest
}: IconToggleProps) {
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    onCheckedChange?.(!checked);
  };

  // Botão base — mesmo tamanho/variant da listagem de Departamentos.
  const button = (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className={cn('h-8 w-8', disabled && 'opacity-50 pointer-events-none', className)}
      onClick={handleClick}
      disabled={disabled}
      aria-pressed={checked}
      aria-label={rest['aria-label']}
    >
      {checked ? (
        <ToggleRight className="h-4 w-4 text-emerald-600" />
      ) : (
        <ToggleLeft className="h-4 w-4 text-muted-foreground" />
      )}
    </Button>
  );

  // Sem tooltip configurado → retorna apenas o botão.
  if (!tooltipOn && !tooltipOff) return button;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent>{checked ? (tooltipOn ?? '') : (tooltipOff ?? '')}</TooltipContent>
    </Tooltip>
  );
}
